import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { TFunction } from 'i18next';
import styled from 'styled-components';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { Study } from '../../types/member';
import type { InternationalCommittee } from '../../types/committee';
import PersonalInfoSection from '../registration/PersonalInfoSection';
import ProfessionalActivitiesSection from '../registration/ProfessionalActivitiesSection';
import WorkDetailsSection from '../registration/WorkDetailsSection';
import StudiesSection from '../registration/StudiesSection';
import MembershipSection from '../registration/MembershipSection';
import ProfileSectionCard from './ProfileSectionCard';
import ProfileCompletenessSummary from './ProfileCompletenessSummary';
import { Button } from '../../design-system/components/Button';
import { Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { borders } from '../../design-system/tokens/borders';
import { typography } from '../../design-system/tokens/typography';
import { hexToRgba } from '../../utils/colorUtils';
import {
  computeOverallCompleteness,
  computeSectionCompleteness,
  type CompletenessStatus,
  type OverallCompleteness,
  type ProfileSectionId,
} from '../../utils/profileSectionCompleteness';
import type { ChangeRequest } from '../../types/changeRequest';
import {
  formatChangeRequestFieldName,
  formatChangeRequestFieldValue,
} from '../../utils/formatChangeField';
import {
  formForApprovedCompleteness,
  latestPendingMemberChanges,
  pendingSectionId,
} from '../../utils/pendingMemberEdits';
import type { MemberFormData } from '../../utils/memberForm';

export type { ProfileSectionId };

const SECTION_KEYS: Record<ProfileSectionId, string[]> = {
  personal: [
    'title',
    'firstNameEn',
    'firstNameGr',
    'lastNameEn',
    'lastNameGr',
    'gender',
    'dateOfBirth',
    'dateOfBirthDay',
    'dateOfBirthMonth',
    'dateOfBirthYear',
    'membershipType',
    'residentialAddressEn',
    'residentialAddressGr',
    'residentialCityEn',
    'residentialCityGr',
    'residentialPostalCode',
    'residentialCountryEn',
    'residentialCountryGr',
    'phone',
    'mobile',
    'contactPreference',
    'organisationNameEn',
    'organisationNameGr',
  ],
  activities: [
    'professionalActivities',
    'isGraduateStudent',
    'isRetired',
    'professionalPath',
    'transferFromIcom',
    'previousCardNumber',
  ],
  work: [
    'professionalTitleEn',
    'professionalTitleGr',
    'workSector',
    'professionalInstitutionNameEn',
    'professionalInstitutionNameGr',
    'professionalAddressEn',
    'professionalAddressGr',
    'professionalCityEn',
    'professionalCityGr',
    'professionalPostalCode',
    'professionalEmail',
    'professionalPhone',
    'website',
    'organisationNameEn',
    'organisationNameGr',
  ],
  studies: ['studies'],
  committees: ['mainCommittee', 'additionalCommittees'],
};

function pickSectionSnapshot<T extends Record<string, unknown>>(
  formData: T,
  sectionId: ProfileSectionId,
  extraKeys: string[] = []
): Partial<T> {
  const keys = [...SECTION_KEYS[sectionId], ...extraKeys];
  const snap: Partial<T> = {};
  for (const key of keys) {
    const value = formData[key];
    (snap as Record<string, unknown>)[key] =
      value != null && typeof value === 'object'
        ? JSON.parse(JSON.stringify(value))
        : value;
  }
  return snap;
}

const SectionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const PendingList = styled.ul`
  margin: ${spacing[2]} 0 0;
  padding-left: ${spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

const PendingTag = styled.span`
  display: inline-block;
  margin-left: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.primary};
`;

const AlertBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borders.radius.lg};
  margin-bottom: ${spacing[5]};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.relaxed};
  border: ${borders.width.thin} solid
    ${props => hexToRgba(props.theme.colors.icomGold || props.theme.colors.accent, 0.35)};
  border-left: 3px solid ${props => props.theme.colors.icomGold || props.theme.colors.accent};
  background: ${props =>
    props.theme.colors.warningBg ||
    hexToRgba(props.theme.colors.icomGold || props.theme.colors.accent, 0.1)};
  color: ${props => props.theme.colors.textSecondary};

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    color: ${props => props.theme.colors.icomGold || props.theme.colors.accent};
  }
`;

const SummaryStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const SummaryLine = styled.div`
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};

  & + & {
    margin-top: 0;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const Chip = styled.span<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 3px ${spacing[2]};
  border-radius: ${borders.radius.sm};
  background: ${props =>
    props.$muted
      ? props.theme.colors.icomWarmGray || props.theme.colors.surfaceHover
      : props.theme.colors.icomLightBlue || hexToRgba(props.theme.colors.primary, 0.1)};
  color: ${props =>
    props.$muted ? props.theme.colors.textMuted || props.theme.colors.textSecondary : props.theme.colors.primary};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.tight};
`;

const EmptyState = styled.div`
  color: ${props => props.theme.colors.textMuted || props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-style: italic;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  text-decoration: underline;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  padding: 0;
  margin-bottom: ${spacing[3]};

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
`;

const SpacedBlock = styled.div`
  margin-bottom: ${spacing[3]};
`;

export interface MemberProfileSectionEditorProps {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  committees: InternationalCommittee[];
  committeesLoadError?: string | null;
  onRetryCommittees?: () => void;
  onOpenCommitteesInfo?: () => void;
  fieldErrors: Record<string, string>;
  onFieldBlur?: (fieldName: string) => void;
  t: TFunction;
  /** Validate a section; return true if valid. Should set fieldErrors/error as needed. */
  validateSection: (sectionId: ProfileSectionId) => boolean;
  /** Persist current form (full payload OK). Resolve on success. */
  onSaveSection: (sectionId: ProfileSectionId) => Promise<number | void>;
  sectionError?: string | null;
  /** Clear parent validation/API error (cancel, reopen, or after user corrects fields). */
  onClearSectionError?: () => void;
  approvalNotice?: string;
  pendingChanges?: ChangeRequest[];
  /** Approved profile. Pending change requests stay visible but are not scored. */
  approvedFormData?: MemberFormData | null;
  /** Extra fields rendered at the top of the personal section (admin email, year). */
  personalExtra?: React.ReactNode;
  /** Fields restored on cancel in addition to the section's own keys. */
  extraSnapshotKeys?: Partial<Record<ProfileSectionId, string[]>>;
  /** Admin membership list includes honorary. */
  includeHonoraryMembership?: boolean;
  /** Show organisation name beside the employing institution. */
  showOrganisationName?: boolean;
  /**
   * Score and present work and studies even when the activity flags are off,
   * so an admin can fill a sparse record without a prior category selection.
   */
  showAllSections?: boolean;
  /** Direct persistence (admin) closes the section on any successful save. */
  directSave?: boolean;
}

function formatName(formData: any): string {
  const first = formData.firstNameEn || formData.firstNameGr || '';
  const last = formData.lastNameEn || formData.lastNameGr || '';
  return [first, last].filter(Boolean).join(' ') || '';
}

function formatAddress(formData: any): string {
  const city = formData.residentialCityEn || formData.residentialCityGr || '';
  const country = formData.residentialCountryEn || formData.residentialCountryGr || '';
  return [city, country].filter(Boolean).join(', ');
}

export const MemberProfileSectionEditor: React.FC<MemberProfileSectionEditorProps> = ({
  formData,
  setFormData,
  committees,
  committeesLoadError,
  onRetryCommittees,
  onOpenCommitteesInfo,
  fieldErrors,
  onFieldBlur,
  t,
  validateSection,
  onSaveSection,
  sectionError,
  onClearSectionError,
  approvalNotice,
  pendingChanges = [],
  approvedFormData = null,
  personalExtra,
  extraSnapshotKeys,
  includeHonoraryMembership = false,
  showOrganisationName = false,
  showAllSections = false,
  directSave = false,
}) => {
  const [editingSection, setEditingSection] = useState<ProfileSectionId | null>(null);
  const [snapshot, setSnapshot] = useState<Partial<typeof formData> | null>(null);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const working = !!formData.professionalActivities?.working;
  const studying = !!formData.professionalActivities?.studying;
  const retired = !!formData.professionalActivities?.retired;

  const overall = useMemo(() => {
    const scoringForm =
      approvedFormData != null
        ? formForApprovedCompleteness(formData, approvedFormData, pendingChanges)
        : formData;
    const base = computeOverallCompleteness(scoringForm);
    if (!showAllSections) return base;
    const activities = scoringForm.professionalActivities ?? {
      working: false,
      studying: false,
      retired: false,
    };
    const work = computeSectionCompleteness('work', {
      ...scoringForm,
      professionalActivities: { ...activities, working: true },
    });
    const studies = computeSectionCompleteness('studies', {
      ...scoringForm,
      professionalActivities: { ...activities, studying: true },
    });
    const sections = { ...base.sections, work, studies };
    const applicable = Object.values(sections).filter(section => section.applicable);
    const percentage =
      applicable.length === 0
        ? 100
        : Math.round(applicable.reduce((sum, section) => sum + section.percentage, 0) / applicable.length);
    let status: CompletenessStatus;
    if (percentage >= 100) status = 'complete';
    else if (percentage <= 0) status = 'incomplete';
    else status = 'partial';
    const next: OverallCompleteness = { percentage, status, sections };
    return next;
  }, [approvedFormData, formData, pendingChanges, showAllSections]);

  const statusLabel = useCallback(
    (status: CompletenessStatus) => {
      switch (status) {
        case 'complete':
          return t('profile.completeness.statusComplete');
        case 'partial':
          return t('profile.completeness.statusPartial');
        case 'notApplicable':
          return t('profile.completeness.statusNotApplicable');
        case 'incomplete':
        default:
          return t('profile.completeness.statusIncomplete');
      }
    },
    [t]
  );

  const sectionCompletenessProps = useCallback(
    (sectionId: ProfileSectionId) => {
      const section = overall.sections[sectionId];
      return {
        percentage: section.percentage,
        status: section.status,
        statusLabel: statusLabel(section.status),
      };
    },
    [overall.sections, statusLabel]
  );

  // Clear stale committee validation once the user corrects the selection.
  useEffect(() => {
    if (editingSection !== 'committees') return;
    if (
      formData.mainCommittee &&
      (formData.additionalCommittees?.length ?? 0) <= 3
    ) {
      setLocalError(null);
      onClearSectionError?.();
    }
  }, [
    editingSection,
    formData.mainCommittee,
    formData.additionalCommittees?.length,
    onClearSectionError,
  ]);

  const beginEdit = useCallback(
    (sectionId: ProfileSectionId) => {
      setSnapshot(
        pickSectionSnapshot(formData, sectionId, extraSnapshotKeys?.[sectionId] ?? [])
      );
      setEditingSection(sectionId);
      setLocalError(null);
      onClearSectionError?.();
    },
    [formData, extraSnapshotKeys, onClearSectionError]
  );

  const cancelEdit = useCallback(() => {
    if (editingSection && snapshot) {
      setFormData((prev: any) => ({ ...prev, ...snapshot }));
    }
    setEditingSection(null);
    setSnapshot(null);
    setLocalError(null);
    onClearSectionError?.();
  }, [editingSection, snapshot, setFormData, onClearSectionError]);

  const saveEdit = useCallback(
    async (sectionId: ProfileSectionId) => {
      setLocalError(null);
      if (!validateSection(sectionId)) {
        return;
      }
      setSaving(true);
      try {
        const createdCount = (await onSaveSection(sectionId)) ?? 0;
        if (!directSave && createdCount === 0) {
          setLocalError(
            String(
              t('profile.noChangesSubmitted', {
                defaultValue: 'No new change was submitted. The approved profile is unchanged.',
              })
            )
          );
          return;
        }
        setEditingSection(null);
        setSnapshot(null);
        setLocalError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('profile.sectionSaveFailed');
        setLocalError(message);
      } finally {
        setSaving(false);
      }
    },
    [validateSection, onSaveSection, directSave, t]
  );

  const jumpToSection = useCallback((sectionId: ProfileSectionId) => {
    const el = document.getElementById(`profile-section-${sectionId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const otherEditing = (id: ProfileSectionId) =>
    editingSection != null && editingSection !== id;

  const activityChips = [
    working ? t('register.professionalActivityWorking') : null,
    studying ? t('register.professionalActivityStudying') : null,
    retired ? t('register.professionalActivityRetired') : null,
  ].filter(Boolean) as string[];

  const workInstitution =
    formData.professionalInstitutionNameEn ||
    formData.professionalInstitutionNameGr ||
    formData.organisationNameEn ||
    formData.organisationNameGr ||
    '';
  const workTitle =
    formData.professionalTitleEn || formData.professionalTitleGr || '';

  const studiesCount = formData.studies?.length ?? 0;

  const mainCommitteeLabel =
    committees.find(c => c.code === formData.mainCommittee || c.id === formData.mainCommittee)
      ?.nameEn ||
    committees.find(c => c.code === formData.mainCommittee || c.id === formData.mainCommittee)
      ?.nameGr ||
    formData.mainCommittee ||
    '';

  const additionalCount = formData.additionalCommittees?.length ?? 0;
  const displayError = localError || sectionError || null;
  const pendingLatest = useMemo(
    () => latestPendingMemberChanges(pendingChanges),
    [pendingChanges]
  );
  const pendingBySection = useMemo(() => {
    const map: Partial<Record<ProfileSectionId, ChangeRequest[]>> = {};
    for (const cr of pendingLatest) {
      const section = pendingSectionId(cr.fieldName);
      if (!section) continue;
      if (!map[section]) map[section] = [];
      map[section]!.push(cr);
    }
    return map;
  }, [pendingLatest]);
  const name = formatName(formData);
  const address = formatAddress(formData);
  const contactBits = [formData.email, formData.phone || formData.mobile].filter(Boolean);

  return (
    <SectionsStack>
      <ProfileCompletenessSummary
        overall={overall}
        title={t('profile.completeness.overallTitle')}
        hint={t('profile.completeness.overallHint')}
        statusLabel={statusLabel(overall.status)}
        sectionLabels={{
          personal: t('profile.sectionPersonal'),
          activities: t('profile.sectionActivities'),
          work: t('profile.sectionWork'),
          studies: t('profile.sectionStudies'),
          committees: t('profile.sectionCommittees'),
        }}
        getStatusLabel={statusLabel}
        onJumpToSection={jumpToSection}
      />

      {(approvalNotice || pendingLatest.length > 0) && (
        <AlertBanner>
          <InformationCircleIcon aria-hidden />
          <div>
            {approvalNotice && <span>{approvalNotice}</span>}
            {pendingLatest.length > 0 && (
              <PendingList>
                {pendingLatest.map(cr => (
                  <li key={cr.id}>
                    <span>
                      <strong>{formatChangeRequestFieldName(cr.fieldName, t)}</strong>
                      {': '}
                      {String(
                        t('profile.pendingAlongsideApproved', {
                          approved: formatChangeRequestFieldValue(cr.fieldName, cr.oldValue, t),
                          pending: formatChangeRequestFieldValue(cr.fieldName, cr.newValue, t),
                        })
                      )}{' '}
                      <PendingTag>{String(t('profile.pendingValue'))}</PendingTag>
                    </span>
                  </li>
                ))}
              </PendingList>
            )}
          </div>
        </AlertBanner>
      )}

      <ProfileSectionCard
        id="personal"
        title={t('profile.sectionPersonal')}
        description={t('profile.sectionPersonalHint')}
        isEditing={editingSection === 'personal'}
        disabled={otherEditing('personal')}
        saving={saving && editingSection === 'personal'}
        error={editingSection === 'personal' ? displayError : null}
        editLabel={t('profile.editSection')}
        saveLabel={t('profile.saveSection')}
        cancelLabel={t('common.cancel')}
        onEdit={() => beginEdit('personal')}
        onCancel={cancelEdit}
        onSave={() => saveEdit('personal')}
        completeness={sectionCompletenessProps('personal')}
        summary={
          name || address || contactBits.length > 0 || pendingBySection.personal?.length ? (
            <SummaryStack>
              {name && (
                <SummaryLine>
                  <strong>{name}</strong>
                </SummaryLine>
              )}
              <ChipRow>
                {address && <Chip>{address}</Chip>}
                {contactBits.map(bit => (
                  <Chip key={String(bit)}>{String(bit)}</Chip>
                ))}
                {(pendingBySection.personal?.length ?? 0) > 0 && (
                  <Chip>{t('profile.pendingValue', { defaultValue: 'Pending' })}</Chip>
                )}
              </ChipRow>
            </SummaryStack>
          ) : (
            <EmptyState>{t('profile.sectionEmpty')}</EmptyState>
          )
        }
      >
        {personalExtra}
        <PersonalInfoSection
          formData={formData}
          setFormData={setFormData}
          t={t}
          showMembershipType={true}
          showTitleAndGender={true}
          showCriteriaBlock={false}
          includeHonoraryMembership={includeHonoraryMembership}
          fieldErrors={fieldErrors}
          onFieldBlur={onFieldBlur}
        />
      </ProfileSectionCard>

      <ProfileSectionCard
        id="activities"
        title={t('profile.sectionActivities')}
        description={t('profile.sectionActivitiesHint')}
        isEditing={editingSection === 'activities'}
        disabled={otherEditing('activities')}
        saving={saving && editingSection === 'activities'}
        error={editingSection === 'activities' ? displayError : null}
        editLabel={t('profile.editSection')}
        saveLabel={t('profile.saveSection')}
        cancelLabel={t('common.cancel')}
        onEdit={() => beginEdit('activities')}
        onCancel={cancelEdit}
        onSave={() => saveEdit('activities')}
        completeness={sectionCompletenessProps('activities')}
        summary={
          activityChips.length > 0 || pendingBySection.activities?.length ? (
            <ChipRow>
              {activityChips.map(label => (
                <Chip key={label}>{label}</Chip>
              ))}
              {(pendingBySection.activities?.length ?? 0) > 0 && (
                <Chip>{t('profile.pendingValue', { defaultValue: 'Pending' })}</Chip>
              )}
            </ChipRow>
          ) : (
            <EmptyState>{t('profile.sectionEmpty')}</EmptyState>
          )
        }
      >
        <ProfessionalActivitiesSection
          professionalActivities={formData.professionalActivities}
          setFormData={setFormData}
          t={t}
          transferFromIcom={formData.transferFromIcom ?? ''}
          previousCardNumber={formData.previousCardNumber ?? ''}
        />
      </ProfileSectionCard>

      <ProfileSectionCard
        id="work"
        title={t('profile.sectionWork')}
        description={t('profile.sectionWorkHint')}
        isEditing={editingSection === 'work'}
        disabled={otherEditing('work')}
        saving={saving && editingSection === 'work'}
        error={editingSection === 'work' ? displayError : null}
        editLabel={t('profile.editSection')}
        saveLabel={t('profile.saveSection')}
        cancelLabel={t('common.cancel')}
        onEdit={() => beginEdit('work')}
        onCancel={cancelEdit}
        onSave={() => saveEdit('work')}
        completeness={sectionCompletenessProps('work')}
        summary={
          !showAllSections && !working && !(pendingBySection.work?.length) ? (
            <EmptyState>{t('profile.sectionNotApplicable')}</EmptyState>
          ) : workInstitution || workTitle || pendingBySection.work?.length ? (
            <SummaryStack>
              {workInstitution && (
                <SummaryLine>
                  <strong>{workInstitution}</strong>
                </SummaryLine>
              )}
              <ChipRow>
                {workTitle && <Chip>{workTitle}</Chip>}
                {(pendingBySection.work?.length ?? 0) > 0 && (
                  <Chip>{t('profile.pendingValue', { defaultValue: 'Pending' })}</Chip>
                )}
              </ChipRow>
            </SummaryStack>
          ) : (
            <EmptyState>{t('profile.sectionEmpty')}</EmptyState>
          )
        }
      >
        {!showAllSections && !working && (
          <Text size="sm" color="secondary" style={{ marginBottom: spacing[3], display: 'block' }}>
            {String(t('profile.enableWorkHint'))}
          </Text>
        )}
        <WorkDetailsSection
          formData={formData}
          setFormData={setFormData}
          t={t}
          fieldErrors={fieldErrors}
          showCheckboxes={false}
          showOrganisationName={showOrganisationName}
          onFieldBlur={onFieldBlur}
        />
      </ProfileSectionCard>

      <ProfileSectionCard
        id="studies"
        title={t('profile.sectionStudies')}
        description={t('profile.sectionStudiesHint')}
        isEditing={editingSection === 'studies'}
        disabled={otherEditing('studies')}
        saving={saving && editingSection === 'studies'}
        error={editingSection === 'studies' ? displayError : null}
        editLabel={t('profile.editSection')}
        saveLabel={t('profile.saveSection')}
        cancelLabel={t('common.cancel')}
        onEdit={() => beginEdit('studies')}
        onCancel={cancelEdit}
        onSave={() => saveEdit('studies')}
        completeness={sectionCompletenessProps('studies')}
        summary={
          !showAllSections && !studying ? (
            <EmptyState>{t('profile.sectionNotApplicable')}</EmptyState>
          ) : studiesCount > 0 || pendingBySection.studies?.length ? (
            <ChipRow>
              {studiesCount > 0 && (
                <Chip>{t('profile.studiesSummary', { count: studiesCount })}</Chip>
              )}
              {(pendingBySection.studies?.length ?? 0) > 0 && (
                <Chip>{t('profile.pendingValue', { defaultValue: 'Pending' })}</Chip>
              )}
            </ChipRow>
          ) : (
            <EmptyState>{t('profile.sectionEmpty')}</EmptyState>
          )
        }
      >
        {!showAllSections && !studying && (
          <Text size="sm" color="secondary" style={{ marginBottom: spacing[3], display: 'block' }}>
            {String(t('profile.enableStudiesHint'))}
          </Text>
        )}
        <StudiesSection
          studies={formData.studies as Study[]}
          onChange={studies => setFormData((prev: any) => ({ ...prev, studies }))}
          fieldErrors={fieldErrors}
        />
      </ProfileSectionCard>

      <ProfileSectionCard
        id="committees"
        title={t('profile.sectionCommittees')}
        description={t('profile.sectionCommitteesHint')}
        isEditing={editingSection === 'committees'}
        disabled={otherEditing('committees')}
        saving={saving && editingSection === 'committees'}
        error={editingSection === 'committees' ? displayError : null}
        editLabel={t('profile.editSection')}
        saveLabel={t('profile.saveSection')}
        cancelLabel={t('common.cancel')}
        onEdit={() => beginEdit('committees')}
        onCancel={cancelEdit}
        onSave={() => saveEdit('committees')}
        completeness={sectionCompletenessProps('committees')}
        summary={
          mainCommitteeLabel ? (
            <SummaryStack>
              <SummaryLine>
                <strong>{`${String(t('profile.mainCommittee'))}: `}</strong>
                {mainCommitteeLabel}
              </SummaryLine>
              <ChipRow>
                <Chip $muted={additionalCount === 0}>
                  {`${String(t('profile.additionalCommittees'))}: ${
                    additionalCount > 0 ? additionalCount : '—'
                  }`}
                </Chip>
              </ChipRow>
            </SummaryStack>
          ) : (
            <EmptyState>{t('profile.sectionEmpty')}</EmptyState>
          )
        }
      >
        {onOpenCommitteesInfo && (
          <SpacedBlock>
            <LinkButton type="button" onClick={onOpenCommitteesInfo}>
              {String(t('register.aboutCommittees'))}
            </LinkButton>
          </SpacedBlock>
        )}
        {committeesLoadError && (
          <SpacedBlock>
            <Text size="sm" color="error" style={{ display: 'block', marginBottom: 8 }}>
              {String(t('register.committees.loadError'))}
            </Text>
            {onRetryCommittees && (
              <Button type="button" variant="outline" size="sm" onClick={onRetryCommittees}>
                {String(t('common.retry'))}
              </Button>
            )}
          </SpacedBlock>
        )}
        <MembershipSection
          formData={formData}
          setFormData={setFormData}
          committees={committees}
          t={t as (key: string) => string}
          showMembershipType={false}
          showHonoraryCheckbox={false}
          maxAdditionalCommittees={3}
        />
      </ProfileSectionCard>
    </SectionsStack>
  );
};

export default MemberProfileSectionEditor;
